// src/pages/ContractsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import ViewContractModal from "./modals/ViewContractModal";
import CreateContractModal from "./modals/CreateContractModal";
import SetContractPriceModal from "./modals/SetContractPriceModal";
import AssignContractNumberModal from "./modals/AssignContractNumberModal";
import ConvertContractToPdfModal from "./modals/ConvertContractToPdfModal";

export default function ContractsTab({ locale = "kz", profile }) {
  const token = localStorage.getItem("token");

  // ============ state ============
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [assignModal, setAssignModal] = useState({ open: false, contract: null });
  const [convertModal, setConvertModal] = useState({ open: false, contract: null });
  const [priceModal, setPriceModal] = useState({ open: false, contract: null });

  const [q, setQ] = useState("");
  const searchTimerRef = useRef(null);

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [list, setList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [selectedContract, setSelectedContract] = useState(null);
  const [viewingContract, setViewingContract] = useState(false);

  const abortRef = useRef(null);

  // ============ i18n ============
  const langHeader = useMemo(
    () => ({ "Accept-Language": locale === "kz" ? "kk" : "ru" }),
    [locale]
  );

  const t = {
    kz: {
      title: "Келісімшарттар",
      search: "Келісімшарт нөмірі/сарапшы/бұйрық №",
      empty: "Келісімшарттар жоқ",
      view: "Көру",
      order: "Бұйрық ID",
      number: "Нөмір",
      expert: "Сарапшы",
      amount: "Сома",
      status: "Күйі",
      created: "Құрылған",
      loadingError: "Мәліметтерді жүктеу қатесі",
      find: "Іздеу",
      loading: "Жүктелуде...",
      create: "Келісімшарт жасау",
    },
    ru: {
      title: "Договоры",
      search: "Номер/эксперт/№ приказа",
      empty: "Договоров нет",
      view: "Просмотр",
      order: "ID приказа",
      number: "Номер",
      expert: "Эксперт",
      amount: "Сумма",
      status: "Статус",
      created: "Создан",
      loadingError: "Ошибка при загрузке данных",
      find: "Найти",
      loading: "Загрузка...",
      create: "Создать договор",
    },
  }[locale];

  // ============ helpers ============
  const tz = "Asia/Almaty";
  const nf = useMemo(
    () => new Intl.NumberFormat(locale === "kz" ? "kk-KZ" : "ru-RU"),
    [locale]
  );

  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const isEmpty = (v) => v == null || v === "" || v === "null" || v === "undefined";

  const isInternalExpert = (p) => {
    if (!p) return false;
    const INTERNAL_EXPERT_ROLE_IDS = [44]; // при необходимости поправь
    const id = Number(p.role?.id);
    if (INTERNAL_EXPERT_ROLE_IDS.includes(id)) return true;

    const names = [
      p.role?.name, p.role?.slug, p.role?.title, p.role?.label,
      p.position, p.title, p.user_role, p.user_role_slug, p.userRole
    ].filter(Boolean).map(norm);

    const many = []
      .concat(p.roles || [], p.user_roles || [])
      .map((r) => (typeof r === "string" ? r : r?.name || r?.slug || r?.title))
      .filter(Boolean).map(norm);

    const hasIE = (s) =>
      s.includes("internal_expert") ||
      (s.includes("внутрен") && s.includes("эксперт")) ||
      (s.includes("ішкі") && s.includes("сарапшы"));

    return [...names, ...many].some(hasIE);
  };

  const isExternalExpert = (p) => {
    if (!p) return false;
    const EXTERNAL_EXPERT_ROLE_IDS = []; // если знаешь ID роли — добавь
    const id = Number(p.role?.id);
    if (EXTERNAL_EXPERT_ROLE_IDS.includes(id)) return true;

    const fields = [
      p.role?.name, p.role?.slug, p.role?.title, p.role?.label,
      p.position, p.title, p.user_role, p.user_role_slug, p.userRole
    ].filter(Boolean).map(norm);

    const many = []
      .concat(p.roles || [], p.user_roles || [])
      .map((r) => (typeof r === "string" ? r : r?.name || r?.slug || r?.title))
      .filter(Boolean).map(norm);

    const hasEE = (s) =>
      s.includes("external_expert") ||
      (s.includes("внешн") && s.includes("эксперт")) ||
      (s.includes("сыртқы") && s.includes("сарапшы"));

    return [...fields, ...many].some(hasEE);
  };

  const isAccountant = (p) => {
    if (!p) return false;
    if (p.role?.id === 12) return true; // явный ID, если известен
    const roleNames = [
      p.role?.name, p.role?.slug, p.role?.title, p.role?.label,
      p.position, p.title, p.user_role, p.user_role_slug, p.userRole
    ].filter(Boolean).map(norm);
    const list = []
      .concat(p.roles || [])
      .concat(p.user_roles || [])
      .map((r) => (typeof r === "string" ? r : r?.slug || r?.name || r?.title))
      .filter(Boolean)
      .map(norm);
    return [...roleNames, ...list].some((s) => s === "accountant" || s === "moderator_accountant");
  };

  const isRegistrar = (p) => {
    if (!p) return false;
    const name = norm(p.role?.name || "");
    return name.includes("регистр") || name.includes("тірке");
  };

  const needsPrice = (c) =>
    isEmpty(c?.amount_numeric) ||
    isEmpty(c?.amount_text_kz) ||
    isEmpty(c?.amount_text_ru);

  const needsNumber = (c) => isEmpty(c?.contract_number);
  const needsPdf   = (c) => isEmpty(c?.pdf_path); // теперь безопасно

  const formatMoney = (v) => {
    if (v == null) return "—";
    const num =
      typeof v === "string" ? Number(String(v).replace(/[^\d.-]/g, "")) : v;
    if (Number.isNaN(num)) return String(v);
    return nf.format(num);
  };

  const safeDate = (s) => (s ? new Date(String(s).replace(" ", "T")) : null);
  const formatDateTime = (s) => {
    const d = safeDate(s);
    if (!d) return "—";
    return d.toLocaleString(locale === "kz" ? "kk-KZ" : "ru-RU", { timeZone: tz });
  };

  const buildPagination = (current, total) => {
    if (!total || total < 2) return [1];
    const s = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
    const pages = [...s].filter(n => n >= 1 && n <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < pages.length; i++) {
      const n = pages[i];
      const prev = pages[i - 1];
      if (i > 0 && n - prev > 1) out.push("...");
      out.push(n);
    }
    return out;
  };

  // ============ api ============
  const parseContractsResponse = (res) => {
    const body = res?.data ?? {};

    if (Array.isArray(body)) return { rows: body, current: 1, last: 1 };

    if (Array.isArray(body.data) && !body?.data?.data)
      return { rows: body.data, current: 1, last: 1 };

    if (Array.isArray(body?.data?.data)) {
      return {
        rows: body.data.data,
        current: body.data.current_page ?? 1,
        last: body.data.last_page ?? 1,
      };
    }

    if (Array.isArray(body.rows)) {
      return {
        rows: body.rows,
        current: body?.meta?.current_page ?? 1,
        last: body?.meta?.last_page ?? 1,
      };
    }

    if (Array.isArray(body.contracts)) {
      return {
        rows: body.contracts,
        current: body.current_page ?? 1,
        last: body.last_page ?? 1,
      };
    }

    return { rows: [], current: 1, last: 1 };
  };

  const fetchContracts = async (p = 1, query = q) => {
    setLoading(true);
    setErr("");

    try { abortRef.current?.abort(); } catch {}

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const params = { page: p };
      if (query) {
        params.q = query;
        params.search = query;
      }

      const res = await axios.get("/contracts/my", {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
        params,
        signal: controller.signal,
      });

      const { rows, current, last } = parseContractsResponse(res);
      setList(rows || []);
      setPage(current || 1);
      setLastPage(last || 1);
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {

        setErr(t.loadingError);
      }
    } finally {
      setLoading(false);
    }
  };

  const openContractView = async (contractId) => {
    try {
      const res = await axios.get(`/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}`, ...langHeader },
      });
      const c = res.data;

      // external_expert — всегда сразу просмотр/подписание
      if (isExternalExpert(profile)) {
        setSelectedContract(c);
        setViewingContract(true);
        return;
      }

      // moderator_accountant — сперва цена
      if (isAccountant(profile) && needsPrice(c)) {
        setPriceModal({ open: true, contract: c });
        return;
      }

      // moderator_registrar — сперва номер
      if (isRegistrar(profile) && needsNumber(c)) {
        setAssignModal({ open: true, contract: c });
        return;
      }

      // internal_expert — сперва конвертнуть в PDF
      if (isInternalExpert(profile) && needsPdf(c)) {
        setConvertModal({ open: true, contract: c });
        return;
      }

      // Остальные — обычный просмотр
      setSelectedContract(c);
      setViewingContract(true);
    } catch (e) {

      alert(t.loadingError);
    }
  };

  // ============ effects ============
  useEffect(() => {
    setPage(1);
    fetchContracts(1, q);
    return () => {
      try { abortRef.current?.abort(); } catch {}
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    fetchContracts(page, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchContracts(1, q);
    }, 350);
    return () => searchTimerRef.current && clearTimeout(searchTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // ============ ui ============
  return (
    <div>
      {/* Верхняя панель: поиск + (опц.) кнопка создать */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 12, justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), fetchContracts(1))}
            placeholder={t.search}
            style={{ width: 360, padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />
          <button onClick={() => { setPage(1); fetchContracts(1); }} className="btn-primary">
            {t.find}
          </button>
        </div>

        {!isExternalExpert(profile) && (
          <button className="btn-primary" onClick={() => setIsCreateOpen(true)}>
            {t.create}
          </button>
        )}
      </div>

      {!!err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}

      <div className="orders-table-wrap">
        <table className="custom-table">
          <thead>
            <tr>
              <th>{t.number}</th>
              <th>{t.expert}</th>
              <th>{t.order}</th>
              <th>{t.amount}</th>
              <th>{t.status}</th>
              <th>{t.created}</th>
              <th style={{ width: 140 }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: 20, opacity: 0.7 }}>{t.loading}</td>
              </tr>
            )}

            {!loading && list.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 20 }}>{t.empty}</td>
              </tr>
            )}

            {!loading && list.map((c) => {
              const status = (c.status || "draft").toLowerCase();
              const number =
                c.contract_number ?? c.number ?? c.contractNo ?? "—";
              const expertName =
  c.external_expert_name ??
  c.expert?.full_name ??
  c.expert_full_name ??
  c.external_expert?.name ?? // ← добавлено .name
  c.expert?.name ??           // ← добавлено .name для совместимости
  "—";

              const orderId = c.order_id ?? c.order?.id ?? c.orderId ?? "—";
              const amount = c.amount_numeric ?? c.amount ?? c.total ?? c.sum ?? null;

              return (
                <tr key={c.id}>
                  <td>{number}</td>
                  <td>{expertName}</td>
                  <td>{orderId}</td>
                  <td>{formatMoney(amount)}</td>
                  <td><span className={`badge status-${status}`}>{c.status || "—"}</span></td>
                  <td>{formatDateTime(c.created_at || c.createdAt)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn-secondary" onClick={() => openContractView(c.id)}>
                      {t.view}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lastPage > 1 && (
        <div style={{ marginTop: 16, display: "flex", gap: 6, justifyContent: "center" }}>
          <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          {buildPagination(page, lastPage).map((it, i) =>
            it === "..." ? (
              <span key={`e${i}`} style={{ padding: "6px 8px", opacity: 0.7 }}>…</span>
            ) : (
              <button key={it} onClick={() => setPage(it)} className={it === page ? "active-page" : ""}>
                {it}
              </button>
            )
          )}
          <button disabled={page === lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
        </div>
      )}

      <CreateContractModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        locale={locale}
        token={token}
        onCreated={() => fetchContracts(1)}
      />

      {viewingContract && selectedContract && (
        <ViewContractModal
          selectedContract={selectedContract}
          setSelectedContract={setSelectedContract}
          profile={profile}
          token={token}
          locale={locale}
          openContractView={openContractView}
          onClose={() => setViewingContract(false)}
        />
      )}

      {priceModal.open && (
        <SetContractPriceModal
          isOpen={priceModal.open}
          onClose={() => setPriceModal({ open: false, contract: null })}
          contract={priceModal.contract}
          token={token}
          locale={locale}
          onSaved={() => openContractView(priceModal.contract.id)}
        />
      )}

      {assignModal.open && (
  <AssignContractNumberModal
    isOpen={assignModal.open}
    onClose={() => setAssignModal({ open: false, contract: null })}
    contract={assignModal.contract}
    token={token}
    locale={locale}
    onSaved={() => {
      setAssignModal({ open: false, contract: null });
      fetchContracts(page, q);
    }}
  />
)}


      {convertModal.open && (
        <ConvertContractToPdfModal
          isOpen={convertModal.open}
          onClose={() => setConvertModal({ open: false, contract: null })}
          contract={convertModal.contract}
          token={token}
          locale={locale}
          onConverted={() => openContractView(convertModal.contract.id)}
        />
      )}
    </div>
  );
}
